import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Package, Loader2, Upload, X, DollarSign,
  Eye, EyeOff, ShoppingBag, Truck, Image as ImageIcon
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import {
  Tabs, TabsList, TabsTrigger, TabsContent
} from '../ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['hats', 'shirts', 'outerwear', 'gear', 'accessories'];
const SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', 'One Size'];
const ORDER_STATUSES = ['paid', 'processing', 'shipped', 'delivered', 'cancelled'];

const StoreManagementTab = () => {
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', category: 'hats', images: [], variants: []
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [variantForm, setVariantForm] = useState({ size: '', color: '', stock: '' });
  const [orderFilter, setOrderFilter] = useState('all');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/store/products`);
      setProducts(data.products);
    } catch { toast.error('Failed to load products'); }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const params = orderFilter !== 'all' ? `?status=${orderFilter}` : '';
      const { data } = await axios.get(`${API_URL}/api/admin/store/orders${params}`, { headers });
      setOrders(data.orders);
    } catch { toast.error('Failed to load orders'); }
  }, [orderFilter]);

  useEffect(() => {
    Promise.all([fetchProducts(), fetchOrders()]).finally(() => setLoading(false));
  }, [fetchProducts, fetchOrders]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/store/products/upload-image`,
        { filename: file.name }, { headers });
      await axios.put(data.upload_url, file, {
        headers: { 'Content-Type': file.type || 'image/jpeg' }
      });
      setProductForm(prev => ({ ...prev, images: [...prev.images, data.image_url] }));
      toast.success('Image uploaded');
    } catch {
      toast.error('Image upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const addVariant = () => {
    if (!variantForm.size && !variantForm.color) return;
    setProductForm(prev => ({
      ...prev,
      variants: [...prev.variants, {
        size: variantForm.size,
        color: variantForm.color,
        stock: parseInt(variantForm.stock) || 0
      }]
    }));
    setVariantForm({ size: '', color: '', stock: '' });
  };

  const removeVariant = (i) => {
    setProductForm(prev => ({
      ...prev, variants: prev.variants.filter((_, idx) => idx !== i)
    }));
  };

  const updateVariantStock = (i, newStock) => {
    setProductForm(prev => ({
      ...prev,
      variants: prev.variants.map((v, idx) => idx === i ? { ...v, stock: parseInt(newStock) || 0 } : v)
    }));
  };

  const openNewProduct = () => {
    setEditingProduct(null);
    setProductForm({ name: '', description: '', price: '', category: 'hats', images: [], variants: [] });
    setVariantForm({ size: '', color: '', stock: '' });
    setShowProductModal(true);
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description || '',
      price: product.price.toString(),
      category: product.category,
      images: product.images ? [...product.images] : [],
      variants: product.variants ? product.variants.map(v => ({ ...v })) : []
    });
    setVariantForm({ size: '', color: '', stock: '' });
    setShowProductModal(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm.name || !productForm.price) {
      toast.error('Name and price are required');
      return;
    }
    // Auto-add any pending variant before saving
    let finalVariants = [...productForm.variants];
    if (variantForm.size || variantForm.color) {
      finalVariants.push({
        size: variantForm.size,
        color: variantForm.color,
        stock: parseInt(variantForm.stock) || 0
      });
      setVariantForm({ size: '', color: '', stock: '' });
    }
    try {
      const payload = {
        ...productForm,
        price: parseFloat(productForm.price),
        variants: finalVariants
      };
      if (editingProduct) {
        await axios.put(`${API_URL}/api/admin/store/products/${editingProduct.id}`, payload, { headers });
        toast.success('Product updated');
      } else {
        await axios.post(`${API_URL}/api/admin/store/products`, payload, { headers });
        toast.success('Product created');
      }
      setShowProductModal(false);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Remove this product?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/store/products/${id}`, { headers });
      toast.success('Product removed');
      fetchProducts();
    } catch { toast.error('Failed to remove product'); }
  };

  const handleUpdateOrderStatus = async (orderId, status, tracking) => {
    try {
      await axios.patch(`${API_URL}/api/admin/store/orders/${orderId}/status`,
        { status, tracking_number: tracking || '' }, { headers });
      toast.success(`Order ${status}`);
      fetchOrders();
    } catch { toast.error('Failed to update order'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="products" className="flex items-center gap-1.5">
            <Package className="w-4 h-4" /> Products ({products.length})
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex items-center gap-1.5">
            <ShoppingBag className="w-4 h-4" /> Orders ({orders.length})
          </TabsTrigger>
        </TabsList>

        {/* Products Tab */}
        <TabsContent value="products" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-display text-lg font-bold uppercase">Products</h3>
            <Button onClick={openNewProduct} data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Product
            </Button>
          </div>

          {products.length === 0 ? (
            <div className="text-center py-12 border rounded-lg">
              <Package className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No products yet. Add your first product!</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {products.map(product => (
                <div key={product.id} className="flex items-center gap-4 border rounded-lg p-3" data-testid={`admin-product-${product.id}`}>
                  {product.images?.[0] ? (
                    <img src={product.images[0]} alt={product.name} className="w-16 h-16 rounded object-cover" />
                  ) : (
                    <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
                      <Package className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-bold truncate">{product.name}</p>
                      <Badge variant="outline" className="text-xs">{product.category}</Badge>
                    </div>
                    <p className="text-sm text-primary font-bold">${product.price.toFixed(2)} CAD</p>
                    <p className="text-xs text-muted-foreground">
                      {product.variants?.length || 0} variant(s) · 
                      Total stock: {product.variants?.reduce((s, v) => s + (v.stock || 0), 0) || 0}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEditProduct(product)} data-testid={`edit-product-${product.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteProduct(product.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          <div className="flex justify-between items-center flex-wrap gap-2">
            <h3 className="font-display text-lg font-bold uppercase">Orders</h3>
            <Select value={orderFilter} onValueChange={setOrderFilter}>
              <SelectTrigger className="w-[160px]" data-testid="order-filter">
                <SelectValue placeholder="Filter orders" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Orders</SelectItem>
                {ORDER_STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {orders.length === 0 ? (
            <div className="text-center py-12 border rounded-lg">
              <ShoppingBag className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No orders yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map(order => (
                <Card key={order.id} data-testid={`order-${order.id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start flex-wrap gap-2 mb-3">
                      <div>
                        <p className="font-bold text-sm">{order.user_name || order.user_email}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(order.created_at).toLocaleDateString()} · Order #{order.id.slice(0, 8)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={
                          order.status === 'paid' ? 'default' :
                          order.status === 'shipped' ? 'secondary' :
                          order.status === 'delivered' ? 'outline' : 'destructive'
                        }>
                          {order.status}
                        </Badge>
                        <span className="font-bold">${order.total?.toFixed(2)} CAD</span>
                      </div>
                    </div>
                    <div className="text-sm space-y-1 mb-3">
                      {order.items?.map((item, i) => (
                        <p key={i} className="text-muted-foreground">
                          {item.quantity}x {item.product_name}
                          {item.size && ` (${item.size})`}
                          {item.color && ` - ${item.color}`}
                        </p>
                      ))}
                    </div>
                    {order.shipping_address && (
                      <p className="text-xs text-muted-foreground mb-3">
                        <Truck className="w-3 h-3 inline mr-1" />
                        {[order.shipping_address.line1, order.shipping_address.city,
                          order.shipping_address.state, order.shipping_address.postal_code,
                          order.shipping_address.country].filter(Boolean).join(', ')}
                      </p>
                    )}
                    {order.status !== 'delivered' && order.status !== 'cancelled' && (
                      <div className="flex gap-2 flex-wrap">
                        {order.status === 'paid' && (
                          <Button size="sm" variant="outline" onClick={() => handleUpdateOrderStatus(order.id, 'processing')}>
                            Mark Processing
                          </Button>
                        )}
                        {(order.status === 'paid' || order.status === 'processing') && (
                          <Button size="sm" onClick={() => {
                            const tracking = window.prompt('Enter tracking number (optional):');
                            handleUpdateOrderStatus(order.id, 'shipped', tracking);
                          }}>
                            <Truck className="w-3 h-3 mr-1" /> Mark Shipped
                          </Button>
                        )}
                        {order.status === 'shipped' && (
                          <Button size="sm" variant="outline" onClick={() => handleUpdateOrderStatus(order.id, 'delivered')}>
                            Mark Delivered
                          </Button>
                        )}
                      </div>
                    )}
                    {order.tracking_number && (
                      <p className="text-xs mt-2">Tracking: <span className="font-mono">{order.tracking_number}</span></p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Product Modal */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Product Name *</Label>
              <Input value={productForm.name} onChange={e => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Elevated Arena Snapback" data-testid="product-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <textarea value={productForm.description}
                onChange={e => setProductForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Product description..."
                className="w-full h-20 p-3 border rounded-md text-sm resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Price (CAD) *</Label>
                <Input type="number" step="0.01" value={productForm.price}
                  onChange={e => setProductForm(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="39.99" data-testid="product-price-input" />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={productForm.category}
                  onValueChange={v => setProductForm(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Images */}
            <div>
              <Label>Images</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {productForm.images.map((url, i) => (
                  <div key={i} className="relative w-20 h-20">
                    <img src={url} alt="" className="w-full h-full object-cover rounded" />
                    <button className="absolute -top-1 -right-1 bg-destructive text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      onClick={() => setProductForm(prev => ({ ...prev, images: prev.images.filter((_, idx) => idx !== i) }))}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <label className="w-20 h-20 border-2 border-dashed rounded flex items-center justify-center cursor-pointer hover:border-primary/50">
                  {uploadingImage ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5 text-muted-foreground" />}
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </label>
              </div>
            </div>

            {/* Variants */}
            <div>
              <Label>Variants (Size / Color / Stock)</Label>
              <div className="space-y-2 mt-1">
                {productForm.variants.map((v, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm border rounded px-2 py-1">
                    <span className="flex-1">{v.size || '-'} / {v.color || '-'}</span>
                    <Input type="number" value={v.stock} onChange={e => updateVariantStock(i, e.target.value)}
                      className="w-20 h-7 text-sm" data-testid={`variant-stock-${i}`} />
                    <span className="text-xs text-muted-foreground">stock</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeVariant(i)}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Select value={variantForm.size} onValueChange={v => setVariantForm(prev => ({ ...prev, size: v }))}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Size" /></SelectTrigger>
                    <SelectContent>
                      {SIZES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Color" value={variantForm.color}
                    onChange={e => setVariantForm(prev => ({ ...prev, color: e.target.value }))}
                    className="flex-1" />
                  <Input type="number" placeholder="Stock" value={variantForm.stock}
                    onChange={e => setVariantForm(prev => ({ ...prev, stock: e.target.value }))}
                    className="w-20" />
                  <Button variant="outline" onClick={addVariant} data-testid="add-variant-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Select size, enter color & stock, then click "Add" for each variant. Unsaved entries are auto-added on save.</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductModal(false)}>Cancel</Button>
            <Button onClick={handleSaveProduct} data-testid="save-product-btn">
              {editingProduct ? 'Update Product' : 'Create Product'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreManagementTab;
